/**
 * lib/share-link — переиспользуемый модуль шаринга ссылки на реплей.
 *
 * Общее поведение «скопировать ссылку на партию» (replays-ghosts.md §2.5 / AC-5):
 * строит абсолютный URL вида `<origin>/r/<short_id>` из серверного `replay_url`
 * (который сервер отдаёт как путь `/r/<short_id>` или уже абсолютный) и кладёт в
 * буфер обмена. Живёт в lib/ (НЕ копируется по экранам) — owner-directive
 * 2026-05-30 «общее поведение — в модуль». Используется result-screen «Поделиться»;
 * любой будущий surface с шарингом импортирует тот же модуль.
 *
 * Clipboard API асинхронный и может быть запрещён (insecure context / permission).
 * `copyShareLink` возвращает Promise<boolean> — caller показывает toast по исходу.
 * Fallback на `document.execCommand('copy')` для старых / non-secure контекстов.
 */

/**
 * Build the absolute share URL from the server `replay_url`. The server returns
 * a path (`/r/<short_id>`) or an absolute URL; either way we resolve it against
 * the current origin so the copied link is shareable (AC-5 «стабильная ссылка»).
 */
export function buildShareUrl(replayUrl: string): string {
  if (!replayUrl) return '';
  if (typeof window === 'undefined') return replayUrl;
  try {
    // `new URL(input, base)` returns `input` verbatim if it's already absolute,
    // else resolves the path against the origin.
    return new URL(replayUrl, window.location.origin).toString();
  } catch {
    return replayUrl;
  }
}

/**
 * Copy `text` to the clipboard. Returns `true` on success. Prefers the async
 * Clipboard API; falls back to a hidden-textarea `execCommand('copy')` for
 * insecure contexts where `navigator.clipboard` is unavailable.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text || typeof navigator === 'undefined') return false;
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission denied / insecure context — fall through to execCommand.
  }
  return legacyCopy(text);
}

/**
 * Build the share URL and copy it in one call (the common result-screen path).
 * Returns the discriminated outcome so the caller can toast appropriately.
 */
export async function copyShareLink(
  replayUrl: string,
): Promise<{ ok: true; url: string } | { ok: false }> {
  const url = buildShareUrl(replayUrl);
  if (!url) return { ok: false };
  const copied = await copyToClipboard(url);
  return copied ? { ok: true, url } : { ok: false };
}

function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
